import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
  CellYesNoV8,
  CellFloatCustomV8,
  BootstrapSwitchButton,
  CellYesNoReverseColorsV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
} from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { ContributionStates } from 'shared-pages/shared/constants';
import { promiseShowCnt2ErpInfo } from '../modal-cnt2erp-promise';
import { FaExchangeAlt, FaEye, FaHistory, FaListAlt } from 'react-icons/fa';
import { FileRepoLink } from 'shared-pages/components/filerepo-link';
/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    setIsInvoiced,
    setReleaseToErp,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const gr = i18n.language === 'gr';

  const [updateCollector] = useMutation(gqlUpdate);
  const [addNewCollector] = useMutation(gqlAdd);
  const [deleteCollector] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Contribution'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'name',
              header: t('Υπόχρεος'),
              columnName: t('Υπόχρεος'),
              accessorKey: 'contributor.name',
              orderByField: 'contributor.name',
              cell: TableCellV8,
              size: 50,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'year',
        header: t('Έτος'),
        columnName: t('Έτος'),
        accessorKey: 'year',
        orderByField: 'year',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'contributionperiod.name',
        header: t('Περίοδος'),
        columnName: t('Περίοδος'),
        accessorKey: 'contributionperiod.name',
        orderByField: 'contributionperiod.name',
        cell: ({ row }) => {
          return <div>{t(row.original.contributionperiod.name)}</div>;
        },
        size: 15,
        meta: { type: 'text' },
      },
      {
        id: 'contributionstate.name',
        header: t('Κατάσταση'),
        columnName: t('Κατάσταση'),
        accessorKey: 'contributionstate.name',
        orderByField: 'contributionstate.name',
        cell: TableCellV8,
        size: 15,
        meta: { type: 'text' },
      },
      {
        id: gr ? 'series.name' : 'series.enName',
        header: t('Σειρά'),
        columnName: t('Σειρά'),
        accessorKey: gr ? 'series.name' : 'series.enName',
        orderByField: gr ? 'series.name' : 'series.enName',
        cell: TableCellV8,
        size: 25,
        meta: { type: 'text' },
      },
      {
        id: 'declarationDate',
        header: t('Ημ/νία Υποβολής'),
        columnName: t('Ημ/νία Υποβολής'),
        accessorKey: 'declarationDate',
        orderByField: 'declarationDate',
        ...dateColumn(t),
      },
      {
        id: 'totalCost',
        header: t('Εισφορές'),
        columnName: t('Εισφορές'),
        accessorKey: 'totalCost',
        orderByField: 'totalCost',
        cell: CellFloatCustomV8(10, 2),
        size: 30,
      },
      {
        id: 'isCanceled',
        header: t('Ακυρωμένη'),
        columnName: t('Ακυρωμένη'),
        accessorKey: 'isCanceled',
        orderByField: 'isCanceled',
        cell: CellYesNoReverseColorsV8,
        size: 30,
      },
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'releaseToErp',
              header: t('Διαθέσιμη στο ERP'),
              columnName: t('Διαθέσιμη στο ERP'),
              accessorKey: 'releaseToErp',
              orderByField: 'releaseToErp',
              cell: (props) => {
                const { getValue, row } = props;
                const showErpInfo =
                  Array.isArray(row.original.cntContributionSend2Erp) &&
                  row.original.cntContributionSend2Erp.length > 0;
                return (
                  <div className="text-center d-flex flex-column align-items-center">
                    {row.original.releaseToErp === true ||
                    row.original.statusId !== ContributionStates.Saved ? (
                      <div>
                        <CellYesNoV8 onlabel={t('ΝΑΙ')} offlabel={t('ΟΧΙ')} {...props} />
                        {row.original.erpEntityCode && <div>{row.original.erpEntityCode}</div>}
                      </div>
                    ) : (
                      <BootstrapSwitchButton
                        checked={row.original.releaseToErp || false}
                        disabled={row.original.releaseToErp === true}
                        size="xs"
                        onlabel={t('ΝΑΙ')}
                        offlabel={t('ΟΧΙ')}
                        onChange={async () =>
                          await setReleaseToErp(row.original.id, !row.original.releaseToErp)
                        }
                        onstyle={'success'}
                        offstyle={'danger'}
                      />
                    )}
                    {showErpInfo && (
                      <button
                        type="button"
                        className="btn btn-sm p-0 text-secondary border-0 bg-transparent shadow-none text-decoration-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          promiseShowCnt2ErpInfo({ contributionId: row.original.id });
                        }}
                        title={t('logs επικοινωνίας με erp')}
                        aria-label={t('logs επικοινωνίας με erp')}
                      >
                        <FaExchangeAlt size={14} />
                        &nbsp;erp
                      </button>
                    )}
                  </div>
                );
              },
              size: 30,
            },
            {
              id: 'isInvoiced',
              header: t('Τιμολογημένη'),
              columnName: t('Τιμολογημένη'),
              accessorKey: 'isInvoiced',
              orderByField: 'isInvoiced',
              cell: (props) => {
                const { getValue, row } = props;
                return (
                  <div className="text-center">
                    {row.original.statusId !== ContributionStates.Saved ? (
                      <CellYesNoV8 onlabel={t('ΝΑΙ')} offlabel={t('ΟΧΙ')} {...props} />
                    ) : (
                      <BootstrapSwitchButton
                        checked={row.original.isInvoiced || false}
                        disabled={row.original.statusId !== ContributionStates.Saved}
                        size="xs"
                        onlabel={t('ΝΑΙ')}
                        offlabel={t('ΟΧΙ')}
                        onChange={async () =>
                          await setIsInvoiced(row.original.id, !row.original.isInvoiced)
                        }
                        onstyle={
                          row.original.statusId !== ContributionStates.Saved ? 'dark' : 'success'
                        }
                        offstyle={
                          row.original.statusId !== ContributionStates.Saved ? 'dark' : 'danger'
                        }
                      />
                    )}
                  </div>
                );
              },
              size: 25,
            },
            {
              id: 'invoiceFileId',
              header: t('Τιμολόγιο'),
              columnName: t('Τιμολόγιο'),
              accessorKey: 'invoiceFileId',
              orderByField: 'invoiceFileId',
              cell: (props) => {
                const { getValue, row } = props;

                return (
                  <>
                    {row.original.invoiceFileId && (
                      <FileRepoLink
                        fileId={row.original.invoiceFileId}
                        fileName={row.original.invoiceFile.fileName}
                      />
                    )}
                  </>
                );
              },
              size: 25,
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="collectors-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Collectors - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewCollector({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addCollectortype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateCollector({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Συλλέκτη <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteCollector({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Συλλέκτης');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
